export type ApplicationStatus = "none" | "applied" | "skip" | "undecided";
export type AttendanceStatus = "none" | "going" | "stream" | "skip" | "undecided";

export type FanStatusRow = {
  eventId: string;
  notificationEnabled: boolean;
  applicationStatus: ApplicationStatus;
  attendanceStatus: AttendanceStatus;
};

const CLIENT_KEY = "fan_notify_client_id_v1";
const SUB_KEY = "fan_push_subscription_id_v1";
const GLOBAL_NOTIFY_KEY = "fan_global_notify_enabled_v1";

export function getFanClientId(): string {
  try {
    let id = localStorage.getItem(CLIENT_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(CLIENT_KEY, id);
    }
    return id;
  } catch {
    return "anonymous";
  }
}

export function getStoredSubscriptionId(): string | null {
  try {
    return localStorage.getItem(SUB_KEY);
  } catch {
    return null;
  }
}

export function setStoredSubscriptionId(id: string | null): void {
  try {
    if (id) localStorage.setItem(SUB_KEY, id);
    else localStorage.removeItem(SUB_KEY);
  } catch {
    /* ignore */
  }
}

export function getGlobalNotifyEnabled(): boolean {
  try {
    const v = localStorage.getItem(GLOBAL_NOTIFY_KEY);
    if (v === "0") return false;
    if (v === "1") return true;
    return Boolean(getStoredSubscriptionId());
  } catch {
    return false;
  }
}

export function setGlobalNotifyEnabled(enabled: boolean): void {
  try {
    localStorage.setItem(GLOBAL_NOTIFY_KEY, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function isIosSafari(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
}

export function isStandalonePwa(): boolean {
  try {
    return window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

async function urlBase64ToArrayBuffer(base64String: string): Promise<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  if (out.length !== 65 || out[0] !== 4) {
    throw new Error("VAPID公開鍵の形式が不正です");
  }
  return out.buffer.slice(out.byteOffset, out.byteOffset + out.byteLength);
}

export async function enableWebPush(): Promise<{ subscriptionId: string | null; error?: string }> {
  try {
    if (!window.isSecureContext) {
      return { subscriptionId: null, error: "通知はHTTPSのページでのみ使えます" };
    }
    if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
      return { subscriptionId: null, error: "このブラウザは通知に対応していません" };
    }
    if (isIosSafari() && !isStandalonePwa()) {
      return {
        subscriptionId: null,
        error: "iPhoneで通知を受け取るには、このサイトをホーム画面に追加してから通知をONにしてください。",
      };
    }

    const perm = await Notification.requestPermission();
    if (perm !== "granted") return { subscriptionId: null, error: "通知が許可されませんでした。ブラウザ設定で通知を許可してください。" };

    const vapidRes = await fetch("/api/public/push/vapid-key", { cache: "no-store" });
    const vapid = (await vapidRes.json()) as { publicKey?: string; enabled?: boolean };
    if (!vapid.enabled || !vapid.publicKey) {
      return { subscriptionId: null, error: "サーバー側の通知設定が未完了です（VAPIDキー）" };
    }

    const reg = await navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" });
    await reg.update().catch(() => undefined);
    const ready = await navigator.serviceWorker.ready;
    const appKey = await urlBase64ToArrayBuffer(vapid.publicKey.trim());
    let sub = await ready.pushManager.getSubscription();
    if (!sub) {
      try {
        sub = await ready.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
      } catch {
        const old = await ready.pushManager.getSubscription().catch(() => null);
        if (old) await old.unsubscribe().catch(() => false);
        sub = await ready.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: appKey });
      }
    }

    const json = sub.toJSON();
    const clientId = getFanClientId();
    const res = await fetch("/api/public/push/subscribe", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        clientId,
        subscription: {
          endpoint: json.endpoint,
          keys: json.keys,
        },
      }),
    });
    if (!res.ok) return { subscriptionId: null, error: "購読の保存に失敗しました" };
    const data = (await res.json()) as { subscriptionId?: string };
    const subscriptionId = data.subscriptionId ?? null;
    setStoredSubscriptionId(subscriptionId);
    return { subscriptionId };
  } catch (e) {
    return { subscriptionId: null, error: `通知の登録に失敗しました: ${e instanceof Error ? e.message : String(e)}` };
  }
}

export async function fetchFanStatuses(eventIds: string[]): Promise<Record<string, FanStatusRow>> {
  const clientId = getFanClientId();
  if (!eventIds.length) return {};
  const q = new URLSearchParams({ clientId, eventIds: eventIds.join(",") });
  const res = await fetch(`/api/public/fan-status?${q}`);
  if (!res.ok) return {};
  const data = (await res.json()) as { statuses?: Record<string, FanStatusRow> };
  return data.statuses ?? {};
}

export async function patchFanStatus(input: {
  eventId: string;
  notificationEnabled?: boolean;
  applicationStatus?: ApplicationStatus;
  attendanceStatus?: AttendanceStatus;
  subscriptionId?: string | null;
}): Promise<FanStatusRow | null> {
  const res = await fetch("/api/public/fan-status", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      clientId: getFanClientId(),
      subscriptionId: getStoredSubscriptionId(),
      ...input,
    }),
  });
  if (!res.ok) return null;
  const data = (await res.json()) as { status?: FanStatusRow };
  return data.status ?? null;
}
